/**
 * Starter typography primitives.
 *
 * Each component maps to one of the design system's `.t-*` classes.
 * All forward refs, spread remaining
 * HTML/SVG props, and style `<em>` children in the brand accent for
 * Display/Heading (the design's signature greeting treatment).
 */
import {
  forwardRef,
  type AnchorHTMLAttributes,
  type ElementType,
  type HTMLAttributes,
  type ReactNode,
} from 'react';
import styles from './typography.module.css';

function cx(...names: Array<string | false | undefined | null>): string {
  return names.filter(Boolean).join(' ');
}

/* ── Display ──────────────────────────────────────────────────────── */

export interface DisplayProps extends HTMLAttributes<HTMLHeadingElement> {
  /** Override the rendered tag — defaults to `h1`. */
  readonly as?: 'h1' | 'h2' | 'p' | 'div';
  readonly children?: ReactNode;
}

export const Display = forwardRef<HTMLHeadingElement, DisplayProps>(
  function Display({ as: Tag = 'h1', className, children, ...rest }, ref) {
    const Component = Tag as ElementType;
    return (
      <Component ref={ref} className={cx(styles.display, className)} {...rest}>
        {children}
      </Component>
    );
  },
);

/* ── Heading (level 1..4) ─────────────────────────────────────────── */

const HEADING_LEVEL_CLASS: Record<1 | 2 | 3 | 4, string> = {
  1: styles.h1 ?? '',
  2: styles.h2 ?? '',
  3: styles.h3 ?? '',
  4: styles.h4 ?? '',
};

export interface HeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  /** Heading level — `1`/`2` use Libre Baskerville (display voice), `3`/`4` use Montserrat SemiBold (working titles). */
  readonly level?: 1 | 2 | 3 | 4;
  /** Override the rendered tag — defaults to `h{level}`. */
  readonly as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'div';
  readonly children?: ReactNode;
}

export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
  function Heading({ level = 1, as, className, children, ...rest }, ref) {
    const Tag = (as ?? (`h${level}` as const)) as ElementType;
    return (
      <Tag
        ref={ref}
        className={cx(styles.heading, HEADING_LEVEL_CLASS[level], className)}
        {...rest}
      >
        {children}
      </Tag>
    );
  },
);

/* ── Body ─────────────────────────────────────────────────────────── */

export interface BodyProps extends HTMLAttributes<HTMLParagraphElement> {
  readonly size?: 'md' | 'sm';
  readonly as?: 'p' | 'span' | 'div';
  readonly children?: ReactNode;
}

export const Body = forwardRef<HTMLParagraphElement, BodyProps>(
  function Body({ size = 'md', as: Tag = 'p', className, children, ...rest }, ref) {
    const Component = Tag as ElementType;
    return (
      <Component
        ref={ref}
        className={cx(size === 'sm' ? styles.bodySm : styles.body, className)}
        {...rest}
      >
        {children}
      </Component>
    );
  },
);

/* ── Caption ──────────────────────────────────────────────────────── */

export interface CaptionProps extends HTMLAttributes<HTMLSpanElement> {
  readonly as?: 'span' | 'p' | 'div';
  readonly children?: ReactNode;
}

export const Caption = forwardRef<HTMLSpanElement, CaptionProps>(
  function Caption({ as: Tag = 'span', className, children, ...rest }, ref) {
    const Component = Tag as ElementType;
    return (
      <Component ref={ref} className={cx(styles.caption, className)} {...rest}>
        {children}
      </Component>
    );
  },
);

/* ── Label (uppercase eyebrow) ────────────────────────────────────── */

export interface LabelProps extends HTMLAttributes<HTMLSpanElement> {
  readonly as?: 'span' | 'p' | 'div';
  readonly children?: ReactNode;
}

export const Label = forwardRef<HTMLSpanElement, LabelProps>(
  function Label({ as: Tag = 'span', className, children, ...rest }, ref) {
    const Component = Tag as ElementType;
    return (
      <Component ref={ref} className={cx(styles.label, className)} {...rest}>
        {children}
      </Component>
    );
  },
);

/* ── Stat ─────────────────────────────────────────────────────────── */

export interface StatProps extends HTMLAttributes<HTMLSpanElement> {
  readonly size?: 'md' | 'lg';
  readonly as?: 'span' | 'p' | 'div';
  readonly children?: ReactNode;
}

export const Stat = forwardRef<HTMLSpanElement, StatProps>(
  function Stat({ size = 'md', as: Tag = 'span', className, children, ...rest }, ref) {
    const Component = Tag as ElementType;
    return (
      <Component
        ref={ref}
        className={cx(size === 'lg' ? styles.statLg : styles.stat, className)}
        {...rest}
      >
        {children}
      </Component>
    );
  },
);

/* ── Mono ─────────────────────────────────────────────────────────── */

export interface MonoProps extends HTMLAttributes<HTMLElement> {
  readonly as?: 'code' | 'span' | 'kbd' | 'samp';
  readonly children?: ReactNode;
}

export const Mono = forwardRef<HTMLElement, MonoProps>(
  function Mono({ as: Tag = 'code', className, children, ...rest }, ref) {
    const Component = Tag as ElementType;
    return (
      <Component ref={ref} className={cx(styles.mono, className)} {...rest}>
        {children}
      </Component>
    );
  },
);

/* ── Link ─────────────────────────────────────────────────────────── */

export interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  readonly children?: ReactNode;
}

/**
 * Inline anchor that picks up `--fg-link` and reveals a hairline underline on
 * hover. For SPA navigation, pair with TanStack Router's `Link`:
 *   `<Link component={RouterLink} to="/foo">Learn more</Link>`
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  function Link({ className, children, ...rest }, ref) {
    return (
      <a ref={ref} className={cx(styles.link, className)} {...rest}>
        {children}
      </a>
    );
  },
);
